import React, {useState} from "react";
import PaymentMethodModal from "./modals/PaymentMethodModal";
import CompleteBookingModal from "./modals/CompleteBookingModal";
import CancelBookingModal from "./modals/CancelBookingModal";
import TermsAndConditionsModal from "./modals/TermsAndConditionsModal";
import ReviewPaymentAmounts from "./modals/ReviewPaymentModal";
import {modalTypes} from "../../../../views/booking-overview/BuyerBooking";
import Busy from "../../../Busy";
import {updatePaymentMethodForBooking, completeBooking} from "../../../../views/requests/booking-requests";
import {getErrorMessageForStandardResponse} from "../../../../util/NetworkErrorUtil";
import {ClassNameMap} from "@mui/styles";
import type {Booking} from "../../../../types/Booking";
import type {Account} from "../../../../types/Account";
import {withSnackbar} from "../../../hocs/withSnackbar";
import {FC} from "react";

interface BuyerPendingDialogsProps {
  account: Account;
  bookingBeingActioned: Booking;
  showModal: { show: boolean, type: string };
  setShowModal: (modal: { show: boolean, type: string }) => void;
  handleCloseModals: () => void;
  onDialogSuccess: () => void;
  getAgreementUrl: () => string;
  handleCancelBooking: (booking: Booking) => void;
  classes: ClassNameMap | string;
  snackbarShowMessage: (message: string, variant: string, duration: number) => void;
}

const BuyerPendingDialogs: FC<BuyerPendingDialogsProps> = (props: BuyerPendingDialogsProps) => {

  const {
    account,
    bookingBeingActioned,
    showModal,
    setShowModal,
    handleCloseModals,
    onDialogSuccess,
    getAgreementUrl,
    handleCancelBooking,
    classes,
    snackbarShowMessage
  } = props;

  const [completeBookingSuccess, setCompleteBookingSuccess] = useState(false);
  const [calculateCostObject, setCalculateCostObject] = useState({});
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState();

  const showPaymentModal = showModal.show && showModal.type === modalTypes.payment;
  const showReviewPaymentModal = showModal.show && showModal.type === modalTypes.review;
  const showCompleteBookingModal = showModal.show && showModal.type === modalTypes.complete && completeBookingSuccess;
  const showCancelModal = showModal.show && showModal.type === modalTypes.cancel_request;
  const showTermsModal = showModal.show && showModal.type === modalTypes.termsOnly;

  const handleError = (err) => {
    snackbarShowMessage(getErrorMessageForStandardResponse(err), "error", 1500)
  }

  const handleShowReviewModal = () => {
    setShowModal({show: true, type: modalTypes.review});
  }

  const handleShowCompleteModal = () => {
    setShowModal({show: true, type: modalTypes.complete})
  }

  const handleCompleteBooking = () => {
    const updateAndCompleteBooking = async () => {
      Busy.set(true);
      const data = {
        id: bookingBeingActioned.id,
        buyerAccountId: account.id,
        tosDocUrl: getAgreementUrl(),
        paymentMethodId: selectedPaymentMethod ? selectedPaymentMethod.id : ''
      };
      try {
        await updatePaymentMethodForBooking(data);
        await completeBooking(bookingBeingActioned);
        handleShowCompleteModal();
        setCompleteBookingSuccess(true);
      } catch (err) {
        handleError(err);
      }
      Busy.set(false);
    }
    updateAndCompleteBooking();
  }

  const sendComplete = () => {
    Busy.set(true);
    completeBooking(bookingBeingActioned)
      .then(() => {
        handleShowCompleteModal();
        setCompleteBookingSuccess(true)
      }).catch(handleError)
      .finally(() => Busy.set(false));
  }

  const finishCompleteBooking = () => {
    onDialogSuccess();
    setCompleteBookingSuccess(false);
  }

  return <>
    <PaymentMethodModal
      open={showPaymentModal}
      booking={bookingBeingActioned}
      account={account}
      handleNext={handleShowReviewModal}
      getAgreementUrl={getAgreementUrl}
      setCalculateCostObject={setCalculateCostObject}
      selectedPaymentMethod={selectedPaymentMethod}
      setSelectedPaymentMethod={setSelectedPaymentMethod}
      onClose={handleCloseModals}
    />
    <ReviewPaymentAmounts
      open={showReviewPaymentModal}
      calculateCostObject={calculateCostObject}
      completeBooking={handleCompleteBooking}
      bookingToComplete={bookingBeingActioned}
      onClose={handleCloseModals}
      classes={classes}
    />
    <CompleteBookingModal
      open={showCompleteBookingModal}
      done={finishCompleteBooking}
      classes={classes}
    />
    <CancelBookingModal
      open={showCancelModal}
      onClose={handleCloseModals}
      cancelBooking={handleCancelBooking}
      booking={bookingBeingActioned}
      classes={classes}
    />
    <TermsAndConditionsModal
      open={showTermsModal}
      onClose={handleCloseModals}
      classes={classes}
      completeBooking={sendComplete}
    />
  </>
};

export default withSnackbar(BuyerPendingDialogs);